import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { invalidateProjectAccessQueries } from '@/lib/queryInvalidation';

export interface ProjectTeamWithDetails {
  id: string;
  project_id: string;
  team_id: string;
  role: string;
  invited_by: string | null;
  created_at: string;
  team: {
    id: string;
    name: string;
    icon: string;
    description: string | null;
  };
}

export const useProjectTeams = (projectId: string) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['project-teams', projectId],
    enabled: !!user && !!projectId,
    queryFn: async (): Promise<ProjectTeamWithDetails[]> => {
      const { data, error } = await supabase
        .from('project_team_members')
        .select('id, project_id, team_id, role, invited_by, created_at, teams(id, name, icon, description)')
        .eq('project_id', projectId);
      if (error) throw error;
      return (data ?? []).map((row: any) => ({
        id: row.id,
        project_id: row.project_id,
        team_id: row.team_id,
        role: row.role,
        invited_by: row.invited_by,
        created_at: row.created_at,
        team: row.teams,
      }));
    },
  });
};

export const useAddProjectTeam = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ projectId, teamId, role }: { projectId: string; teamId: string; role: string }) => {
      const { data, error } = await supabase
        .from('project_team_members')
        .insert({ project_id: projectId, team_id: teamId, role, invited_by: user!.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      invalidateProjectAccessQueries(queryClient, vars.projectId);
    },
  });
};

export const useUpdateProjectTeamRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, role, projectId }: { id: string; role: string; projectId: string }) => {
      const { data, error } = await supabase
        .from('project_team_members')
        .update({ role })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      if (!data) throw new Error('Permissão de time não encontrada ou sem autorização para atualizar.');
      return data;
    },
    onSuccess: (_, vars) => {
      invalidateProjectAccessQueries(queryClient, vars.projectId);
    },
  });
};

export const useRemoveProjectTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, projectId }: { id: string; projectId: string }) => {
      const { data, error } = await supabase
        .from('project_team_members')
        .delete()
        .eq('id', id)
        .select('id')
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Permissão de time não encontrada ou sem autorização para remover.');
    },
    onSuccess: (_, vars) => {
      invalidateProjectAccessQueries(queryClient, vars.projectId);
    },
  });
};
